/*
   An engagement's detector profile, edited.

   What Automatic resolves to first for every review filed against this
   engagement. It is meant to be derived from the client's own labelled data —
   `scripts/client_metrics derive-profile` writes `detector_profile.json` — so
   the editor can load that file as well as take settings by hand. Loading
   fills the form and saves nothing: an admin reads what it set and saves it.
   A file sets only the settings it carries; the rest of the form, hand-set or
   not, is kept, and the note names which came from the file.
*/

import { useQuery } from "@tanstack/react-query"
import { FileUp } from "lucide-react"
import * as React from "react"

import {
  DetectorProfileFields,
  detectorProfileOptionsQuery,
  DETECTOR_SETTING_KEYS,
  isAllAuto,
  profileFromDerivedFile,
  settingOf,
  toFullProfile,
  type DetectorProfile,
  type DetectorProfileOptions,
} from "@/components/domain/detector-profile"
import { Button } from "@/components/ui/button"

function sameProfile(a: DetectorProfile, b: DetectorProfile): boolean {
  const fa = toFullProfile(a)
  const fb = toFullProfile(b)
  return (Object.keys(fa) as (keyof typeof fa)[]).every((k) => fa[k] === fb[k])
}

/** Through FileReader rather than `File.text()`, which not every runtime the console is tested in has. */
function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(typeof reader.result === "string" ? reader.result : "")
    }
    reader.onerror = () => {
      reject(reader.error ?? new Error("unreadable file"))
    }
    reader.readAsText(file)
  })
}

async function readDerivedFile(file: File): Promise<DetectorProfile | null> {
  try {
    const parsed: unknown = JSON.parse(await readText(file))
    return profileFromDerivedFile(parsed)
  } catch {
    return null
  }
}

export function EngagementProfileEditor({
  saved,
  onSave,
}: {
  /** The profile as stored. */
  saved: DetectorProfile
  onSave: (profile: DetectorProfile) => Promise<void>
}): JSX.Element {
  const options = useQuery(detectorProfileOptionsQuery())
  const [draft, setDraft] = React.useState<DetectorProfile>(saved)
  const [isSaving, setIsSaving] = React.useState(false)
  const [fileNote, setFileNote] = React.useState<string | null>(null)
  const fileInput = React.useRef<HTMLInputElement>(null)
  const headingId = React.useId()

  // A refreshed row is the new stored profile; the draft follows it.
  React.useEffect(() => {
    setDraft(saved)
  }, [saved])

  const dirty = !sameProfile(draft, saved)

  const save = async () => {
    setIsSaving(true)
    const ok = await onSave(draft).then(
      () => true,
      () => false,
    )
    setIsSaving(false)
    // The note asked for a save; once it is made the note is stale.
    if (ok) setFileNote(null)
  }

  const load = async (file: File, offered: DetectorProfileOptions) => {
    const derived = await readDerivedFile(file)
    if (derived === null) {
      setFileNote(`${file.name} carries no detector settings.`)
      return
    }
    // A value this deployment does not offer is left out: the server would refuse it on save.
    const usable: DetectorProfile = {}
    const ignored: string[] = []
    for (const key of DETECTOR_SETTING_KEYS) {
      const value = derived[key]
      if (value === undefined) continue
      const setting = settingOf(offered, key)
      if (setting?.values.some((v) => v.value === value)) usable[key] = value
      else ignored.push(`${setting?.label ?? key} (${value})`)
    }
    const skipped =
      ignored.length > 0 ? ` Not offered here, so left out: ${ignored.join(", ")}.` : ""
    const fromFile = DETECTOR_SETTING_KEYS.filter((k) => usable[k] !== undefined).map(
      (k) => settingOf(offered, k)?.label ?? k,
    )
    if (fromFile.length === 0) {
      setFileNote(`${file.name} sets nothing this deployment offers.${skipped}`)
      return
    }
    // Onto the draft, not onto all Automatic: a setting the file does not mention keeps what the
    // admin has, rather than being reset without a word.
    setDraft((current) => ({ ...toFullProfile(current), ...usable }))
    const kept = fromFile.length < DETECTOR_SETTING_KEYS.length ? " The others are unchanged." : ""
    setFileNote(
      `Loaded from ${file.name}: ${fromFile.join(", ")}.${kept}${skipped} Review the settings, then save.`,
    )
  }

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 id={headingId} className="text-body-sm font-semibold text-text">
            Detector profile
          </h3>
          <p className="text-caption text-text-3">
            {isAllAuto(saved)
              ? "All automatic: this engagement's reviews use the deployment default."
              : "What Automatic means for this engagement's reviews. A reviewer can still set one at intake."}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={<FileUp />}
          disabled={isSaving || options.data === undefined}
          onClick={() => fileInput.current?.click()}
        >
          Load derived file
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          className="hidden"
          aria-label="Derived detector profile file"
          onChange={(e) => {
            const file = e.currentTarget.files?.[0]
            e.currentTarget.value = ""
            if (file && options.data) void load(file, options.data)
          }}
        />
      </div>

      {options.isError ? (
        <p className="text-caption text-text-3">The detector settings could not be loaded.</p>
      ) : (
        <DetectorProfileFields
          options={options.data}
          value={draft}
          onChange={setDraft}
          autoMeans="default"
          disabled={isSaving}
        />
      )}

      {fileNote && (
        <p role="status" className="text-caption text-text-2">
          {fileNote}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty || options.data === undefined}
          loading={isSaving}
          loadingText="Saving…"
          onClick={() => void save()}
        >
          Save profile
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!dirty || isSaving}
          onClick={() => {
            setDraft(saved)
            setFileNote(null)
          }}
        >
          Discard changes
        </Button>
      </div>
    </section>
  )
}
